/**
 * Preferences › Appearance (settings pocket spec §6): the theme, the surface
 * transparency, the ink colours for agent marks and the user's own marks, and
 * the agent avatar style. All apply as soon as they are picked.
 */
import { useCallback, useEffect, useState } from "react";
import { updateUser } from "@/api/auth";
import {
  ColorPicker,
  PocketRow,
  PocketSection,
  SelectField,
  Slider,
} from "@/components/ui";
import { useAuth } from "@/hooks/use-auth";
import { translate, useT } from "@/i18n";
import {
  AVATAR_STYLE_LABEL_KEY,
  settingsPreferencesMessages,
  THEME_LABEL_KEY,
} from "@/i18n/messages/settings-preferences";
import {
  AGENT_AVATAR_STYLES,
  type AgentAvatarStyleKey,
} from "@/lib/dicebear-styles";
import {
  getStoredThemeChoice,
  parseThemeChoice,
  setThemeChoice,
  THEME_CHOICES,
  THEME_CHANGED_EVENT,
  type ThemeChoice,
} from "@/lib/theme";
import {
  getStoredTransparency,
  parseTransparency,
  setTransparency,
  TRANSPARENCY_CHANGED_EVENT,
} from "@/lib/transparency";
import {
  DEFAULT_INK,
  INK_SWATCHES,
  useAgentInk,
  useInkSettingsStore,
  useUserInk,
} from "@/stores/ink-settings-store";

const inkSwatchLabel = (color: string) =>
  color === DEFAULT_INK
    ? translate(settingsPreferencesMessages, "inkDenkerGreen")
    : translate(settingsPreferencesMessages, "inkColor", { color });

export function AppearancePage() {
  const t = useT(settingsPreferencesMessages);
  const { user, refreshUser } = useAuth();
  const [theme, setTheme] = useState<ThemeChoice>(getStoredThemeChoice);
  const [transparency, setTransparencyState] = useState(getStoredTransparency);
  const agentInk = useAgentInk();
  const userInk = useUserInk();
  const setAgentInk = useInkSettingsStore((s) => s.setAgentInk);
  const setUserInk = useInkSettingsStore((s) => s.setUserInk);
  const [avatarStyle, setAvatarStyle] = useState<AgentAvatarStyleKey>(
    user?.agent_avatar_style || "glass",
  );

  // The web header's theme button changes the theme too.
  useEffect(() => {
    const onThemeChanged = (event: Event) => {
      const next = parseThemeChoice(
        (event as CustomEvent<{ theme?: unknown }>).detail?.theme,
      );
      if (next) setTheme(next);
    };
    window.addEventListener(THEME_CHANGED_EVENT, onThemeChanged);
    return () =>
      window.removeEventListener(THEME_CHANGED_EVENT, onThemeChanged);
  }, []);

  // Other windows and web tabs change the transparency too.
  useEffect(() => {
    const onTransparencyChanged = (event: Event) => {
      const next = parseTransparency(
        (event as CustomEvent<{ value?: number }>).detail?.value,
      );
      if (next !== null) setTransparencyState(next);
    };
    window.addEventListener(TRANSPARENCY_CHANGED_EVENT, onTransparencyChanged);
    return () =>
      window.removeEventListener(
        TRANSPARENCY_CHANGED_EVENT,
        onTransparencyChanged,
      );
  }, []);

  const changeAvatarStyle = useCallback(
    async (style: AgentAvatarStyleKey) => {
      const previous = avatarStyle;
      setAvatarStyle(style);
      try {
        await updateUser({ agent_avatar_style: style });
        await refreshUser();
      } catch {
        setAvatarStyle(previous);
      }
    },
    [avatarStyle, refreshUser],
  );

  return (
    <PocketSection testId="settings-page-preferences-appearance">
      <PocketRow
        testId="settings-row-appearance-theme"
        title={t("theme")}
        trailing={
          <SelectField
            variant="value"
            value={theme}
            options={THEME_CHOICES.map((choice) => ({
              value: choice,
              label: t(THEME_LABEL_KEY[choice]),
            }))}
            onChange={(choice) => {
              setTheme(choice as ThemeChoice);
              setThemeChoice(choice as ThemeChoice);
            }}
            testId="appearance-theme"
            ariaLabel={t("theme")}
          />
        }
      />
      <PocketRow
        testId="settings-row-appearance-transparency"
        title={t("transparency")}
        trailing={
          <Slider
            min={0}
            max={100}
            step={1}
            value={transparency}
            onValueChange={(next) => {
              setTransparencyState(next);
              setTransparency(next);
            }}
            aria-label={t("transparency")}
            className="w-[140px]"
            data-testid="appearance-transparency"
          />
        }
      />
      <PocketRow
        testId="settings-row-appearance-agent-ink"
        title={t("agentInk")}
        trailing={
          <ColorPicker
            value={agentInk}
            onChange={setAgentInk}
            colors={INK_SWATCHES}
            ariaLabel={t("agentInk")}
            swatchLabel={inkSwatchLabel}
            data-testid="appearance-agent-ink"
          />
        }
      />
      <PocketRow
        testId="settings-row-appearance-user-ink"
        title={t("myInk")}
        trailing={
          <ColorPicker
            value={userInk}
            onChange={setUserInk}
            colors={INK_SWATCHES}
            ariaLabel={t("myInk")}
            swatchLabel={inkSwatchLabel}
            data-testid="appearance-user-ink"
          />
        }
      />
      <PocketRow
        testId="settings-row-appearance-avatars"
        title={t("agentAvatars")}
        trailing={
          <SelectField
            variant="value"
            value={avatarStyle}
            options={AGENT_AVATAR_STYLES.map(({ key }) => ({
              value: key,
              label: t(AVATAR_STYLE_LABEL_KEY[key]),
            }))}
            onChange={(style) =>
              void changeAvatarStyle(style as AgentAvatarStyleKey)
            }
            testId="appearance-avatars"
            ariaLabel={t("agentAvatars")}
          />
        }
      />
    </PocketSection>
  );
}
